import { InputControllerTypes } from 'common/enums';
import { IDefaultValue, IRuleParemeters } from 'common/model';
import { uuid } from 'common/helper';
import { KeyValue } from '@angular/common';

// In-band marker on the END of an option label in the form builder ("Other#textbox",
// "Other#textarea"). The marker is stripped from the displayed label and tells the checkbox /
// radio renderers to offer a free-text input for that option:
//   textbox  - single-line input inline beside the label (always visible, enabled once ticked)
//   textarea - multi-line box on its own row under the label, shown only while ticked
// The backend recognises the same markers for import and widget label cleaning.
export enum InputContentDataExtraValueType {
    Textbox = 'textbox',
    Textarea = 'textarea'
}

export type InputContentDataType = KeyValue<string, string | InputContentDataExtraValueType> & { checked?: boolean, extraValueType?: InputContentDataExtraValueType, extraValue?: string, heading?: string }


export const extractValueWithExtraType = (value: string): [string, InputContentDataExtraValueType | undefined] => {
    const [actualVal, extraVal] = splitLastOccurrence(value, '#');

    if (extraVal && Object.values(InputContentDataExtraValueType).includes(extraVal.toLowerCase() as InputContentDataExtraValueType)) {
        return [actualVal, extraVal.toLowerCase() as InputContentDataExtraValueType];
    } else {
        return [value, undefined]
    }
}

function splitLastOccurrence(str, substring): [string, string] {
    const arr = str.split(substring);

    const after = arr.pop();

    const before = arr.join(substring);

    return [before, after];
}

export class ClientInputContentGroup {

    public static readonly KeyInitials = 'question-group';

    public inputContents: ClientInputContent[] = [];
    public uuid = `${ClientInputContentGroup.KeyInitials}-${uuid()}`;

    constructor(

        public readonly id: number,
        public readonly inputContentGroupTitle: string,
        public readonly inputContentGroupDescription: string,
        public readonly pageBreakTitle: string,
        public readonly pageBreak: boolean,
        public readonly sortOrder: number,
        public readonly ruleParemeters?: IRuleParemeters
    ) { }

    public static fromJson(req: IClientEvideGroupHttp, ruleParemeters?: IRuleParemeters): ClientInputContentGroup {
        const group = new this(
            req.id, req.inputContentGroupTitle, req.inputContentGroupDescription,
            req.pageBreakTitle, req.pageBreak, req.sortOrder, ruleParemeters
        );
        if (req.inputContents) {
            group.inputContents.push(...req.inputContents.map(item => ClientInputContent.fromJson(item, ruleParemeters)));
        }
        return group;
    }

}

export class ClientInputContent {
    public description: string;
    public title: string;
    // True for the contact reference field — the client form pre-fills it from the non-rule generator on open.
    public isReferenceField = false;

    constructor(
        public readonly id: number,
        public readonly inputContentId: number,
        public readonly gridVisible: boolean,
        public readonly detailVisible: boolean,
        public readonly gridOrder: number,
        public readonly isMandatory: boolean,
        public readonly isRequired: boolean,
        public readonly isHidden: boolean,
        public readonly hasDefaultValue: boolean,
        public readonly defaultValue: IDefaultValue,
        public readonly isUnique: boolean,
        public readonly isSearch: boolean,
        public readonly minValue: string,
        public readonly maxValue: string,
        public readonly regEx: string,
        public readonly sortOrder: number,
        public readonly inputContentGuid: string,
        public readonly inputContentDescription: string,
        public readonly inputContentTitle: string,
        public readonly dataType: number,
        public readonly inputControllerType: InputControllerTypes,
        public readonly dataTypeName: string,
        public readonly inputControllerTypeName: string,
        public readonly inputContentAnswerName: string,
        public readonly inputDataSourceType: number,
        public readonly inputDataSourceTypeName: string,
        public readonly inputContentAnswerInfos: IClientInputContentAnswerInfoHttp[],
        public readonly inputContentDatas: ReadonlyArray<InputContentDataType>,
        public readonly ruleParemeters?: IRuleParemeters
    ) { }

    public static fromJson(data: IClientInputContentHttp, ruleParemeters?: IRuleParemeters): ClientInputContent {
        const inputContentDatas: ReadonlyArray<InputContentDataType> = data.inputContentDatas.map(
            (data: IClientInputContentDataHttp) => {
                const [value, extraValueType] = extractValueWithExtraType(data.dataValue);
                return { key: data.dataKey, value: value, extraValueType, heading: data.dataHeading || undefined };
            }
        );
        const inputContent = new ClientInputContent(
            data.id, data.inputContentId, data.gridVisible, data.detailVisible, data.gridOrder,
            !!data.isMandatory, !!data.isRequired, !!data.isHidden, !!data.hasDefaultValue, data.defaultValue !== null ? data.defaultValue : '',
            data.isUnique, data.isSearch,
            data.minValue, data.maxValue, data.regEx, data.sortOrder,
            data.inputContentGuid, data.inputContentDescription, data.inputContentTitle, data.dataType,
            data.inputControllerType, data.dataTypeName, data.inputControllerTypeName, data.inputContentAnswerName,
            data.inputDataSourceType, data.inputDataSourceTypeName, data.inputContentAnswerInfos, inputContentDatas,
            ruleParemeters
        );
        inputContent.title = inputContent.inputContentTitle;
        inputContent.description = inputContent.inputContentDescription;
        inputContent.isReferenceField = !!data.isReferenceField;
        return inputContent;
    }

}


export interface IClientEvideGroupHttp {
    id: number;
    inputContentGroupTitle: string;
    inputContentGroupDescription: string;
    pageBreakTitle: string;
    pageBreak: boolean;
    sortOrder: number;
    inputContents: IClientInputContentHttp[];
}

export interface IClientInputContentHttp {
    id: number;
    isReferenceField?: boolean;
    inputContentId: number;
    gridVisible: boolean;
    detailVisible: boolean;
    gridOrder: number;
    isMandatory: boolean;
    isRequired: boolean;
    isHidden: boolean;
    hasDefaultValue: boolean;
    defaultValue: IDefaultValue;
    isUnique: boolean;
    isSearch: boolean;
    minValue: string;
    maxValue: string;
    regEx: string;
    sortOrder: number;
    inputContentGuid: string;
    inputContentDescription: string;
    inputContentTitle: string;
    dataType: number;
    inputControllerType: InputControllerTypes;
    dataTypeName: string;
    inputControllerTypeName: string;
    inputContentAnswerName: string;
    inputDataSourceType: number;
    inputDataSourceTypeName: string;
    inputContentAnswerInfos: IClientInputContentAnswerInfoHttp[];
    inputContentDatas: IClientInputContentDataHttp[];
}

export interface IClientInputContentAnswerInfoHttp {
    dataKey: string;
    dataValue: string;
}

export interface IClientInputContentDataHttp {
    id?: number;
    dataKey: string;
    dataValue: string;
    dataHeading?: string;
}

export interface IClientEvideFormSaveAnswerHttp {
    clientInputContents: IClientInputContentRequestHttp[];
    encodedResponse: string;
    // True when the reference field was left untouched (still the provisional preview) so the server should
    // allocate the real gap-free sequential reference on submit; false when the user typed their own value.
    forceGenerateReference?: boolean;
}

export interface IClientInputContentRequestHttp {
    evideInputContentGuid: string;
    dataStringValues?: string[];
    dataStringValue: string;
}
